import { type ReactNode, useState } from "react";
import styles from "./mobile.module.css";

export interface PhoneAuthShellProps {
  /** The company the customer chose: its name, and its logo (or the app icon). */
  company: { name: string; logoUrl: string };
  /** Shown instead of a logo that doesn't load. */
  fallbackLogoUrl?: string;
  /** "Change" next to the company opens the Choose company sheet; omitted on later steps. */
  onChangeCompany?: () => void;
  /** Above the title in the accent colour, e.g. "Customer app". */
  product: string;
  title: string;
  description?: ReactNode;
  /** Top right on a wide screen: who to call for help. */
  helpLine?: ReactNode;
  /** Fields, notices and links. */
  children?: ReactNode;
  /** The step's buttons: at the bottom of a phone, under the form on a wide screen. */
  actions?: ReactNode;
}

/**
 * The phone sign-in frame (section 09: Phone, SMS Code, PIN, New PIN). On a phone: the company
 * at the top, the step, and the actions at the bottom. On a wide screen it becomes the Auth
 * Shell (Desktop / Sign in): company and help line at the top, a 400 px column, © MajiTap.
 */
export function PhoneAuthShell({
  company,
  fallbackLogoUrl,
  onChangeCompany,
  product,
  title,
  description,
  helpLine,
  children,
  actions,
}: PhoneAuthShellProps) {
  const [broken, setBroken] = useState(false);
  return (
    <div className={styles.auth}>
      <header className={styles.authTop}>
        <div className={styles.authCompany}>
          <img
            className={styles.authLogo}
            src={broken && fallbackLogoUrl ? fallbackLogoUrl : company.logoUrl}
            alt=""
            onError={() => setBroken(true)}
          />
          <span className={styles.authCompanyName}>{company.name}</span>
          {onChangeCompany ? (
            <button type="button" className={styles.link} onClick={onChangeCompany}>
              Change
            </button>
          ) : null}
        </div>
        {helpLine ? <p className={styles.authHelp}>{helpLine}</p> : null}
      </header>
      <main className={styles.authMain}>
        <div className={styles.authHeading}>
          <p className={styles.authProduct}>{product}</p>
          <h1 className={styles.authTitle}>{title}</h1>
          {description ? <div className={styles.authDescription}>{description}</div> : null}
        </div>
        {children ? <div className={styles.authBody}>{children}</div> : null}
        {actions ? <div className={styles.authActions}>{actions}</div> : null}
      </main>
      <footer className={styles.authFooter}>© MajiTap</footer>
    </div>
  );
}

/** A full-width step action, 52 px on a phone (Mobile / Button). Pass to `Button`'s className. */
export const mobileActionClass = styles.action;

/** Arc's SegmentedControl stretched across the width (When, Active/Past). Pass as its className. */
export const fullSegmentsClass = styles.fullSegments;

/** A text link in the accent colour (Change, Forgot your PIN?, Resend). */
export function LinkButton(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button type="button" {...props} className={[styles.link, props.className].filter(Boolean).join(" ")} />;
}
